import { useMemo, useState } from 'react';
import type { PermissionDomain, RoleRow, ScopeType } from '@dru/types';
import { SCOPE_LABEL } from '@dru/types';
import { Eye, Lock, Pencil, Plus, ShieldAlert, Trash2, Users } from 'lucide-react';
import {
  Alert, Button, ConfirmDialog, DataTable, Dialog, ErrorBanner, Field, FilterBar, IconAction, InfoTip, RowActions, SearchInput, SelectField,
  StatusBadge, TextField, Textarea, useToast,
} from '@dru/ui';
import type { Column } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { errorMessage } from '../../../lib/auth';
import { PageHeader, REALM_OF_SCOPE, realmOf, realmOfRole, type Realm } from '../../../layouts/ConsoleShell';
import { NotForYourRole } from './Audit';

/**
 * What each role can do.
 *
 * Roles were seed-time constants, and for a fixed catalogue that was the
 * right call — a permission change was a migration and a deploy, which is a
 * high bar in exactly the right place. It stops being right the moment a
 * customer needs a role the eleven seeded ones do not describe.
 *
 * The editor is built around the fact that this screen changes what people
 * can do to medical records, so it is deliberately more ceremonious than a
 * settings page:
 *
 *   · the matrix shows what a role has, not a list of everything it lacks;
 *   · a permission the editor does not hold cannot be ticked at all, and says
 *     so, rather than failing on save;
 *   · saving needs a written reason, because the consequences of this change
 *     are invisible on the screen that made it;
 *   · the two clinical invariants are enforced by the API, and the ones this
 *     screen can predict are flagged before the save rather than after.
 */
export function Roles() {
  const { permissions } = useAuth();
  if (!(permissions.has('user.role-assign'))) {
    return <NotForYourRole title="Roles" what="Roles are managed by platform administrators." />;
  }
  return <RolesPage />;
}

function RolesPage() {
  const { permissions: held, principal } = useAuth();
  const toast = useToast();

  const roles = useQuery<RoleRow[]>(() => api.get('/admin/roles'), []);
  const catalogue = useQuery<PermissionDomain[]>(() => api.get('/admin/permissions'), []);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<RoleRow | null>(null);
  const canEdit = held.has('system.settings-manage');

  // Bounded to the realm this console administers, the same way the
  // navigation is. A platform administrator hands out the platform's own
  // roles; a company's HR roles are granted inside that company's console, by
  // somebody who can actually see the people holding them. Listing all
  // seventeen here invited a platform operator to edit a role whose holders
  // they cannot see — and `PHYSICIAN` and `EMPLOYEE` are SELF-scoped, held by
  // being one rather than granted at all, so they belong on no such list.
  const realm = realmOf(principal);

  const realmRoles = useMemo(
    () => (roles.data ?? []).filter((r) => realmOfRole(r) === realm),
    [roles.data, realm],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return realmRoles.filter((r) => {
      if (filters.scopeType && r.scopeType !== filters.scopeType) return false;
      if (filters.kind === 'system' && !r.isSystem) return false;
      if (filters.kind === 'custom' && r.isSystem) return false;
      if (!term) return true;
      return r.key.toLowerCase().includes(term) || r.name.toLowerCase().includes(term);
    });
  }, [realmRoles, search, filters]);

  const columns: Column<RoleRow>[] = [
    {
      key: 'name',
      header: 'Role',
      alwaysVisible: true,
      cell: (r) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-neutral-900">{r.name}</span>
            {r.isSystem && (
              <StatusBadge tone="neutral">
                <Lock className="mr-0.5 inline h-3 w-3 align-[-1px]" aria-hidden /> System
              </StatusBadge>
            )}
          </div>
          <div className="truncate text-caption font-normal text-neutral-500">
            {!r.isSystem ? 'Custom role'
              : r.deletable ? 'Built in; can be deleted while nobody holds it'
              : 'The platform depends on it; it cannot be deleted'}
          </div>
        </div>
      ),
    },
    {
      key: 'scope',
      header: 'Reaches',
      cell: (r) => (
        <span className="text-small text-neutral-600">{SCOPE_LABEL[r.scopeType]}</span>
      ),
    },
    {
      key: 'permissions',
      header: 'Permissions',
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.permissionCount}</span>,
    },
    {
      key: 'holders',
      header: 'People',
      align: 'right',
      cell: (r) => (
        <span className={r.holders === 0 ? 'text-caption text-neutral-500' : 'tabular-nums text-neutral-800'}>
          {r.holders === 0 ? 'Nobody' : r.holders}
        </span>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      defaultHidden: true,
      cell: (r) => (
        <StatusBadge tone={r.isSystem ? 'neutral' : 'info'}>
          {r.isSystem ? 'System' : 'Custom'}
        </StatusBadge>
      ),
    },
    // Opening the role is also the row itself; the icon says so. Edit for
    // somebody who can change roles, View for everybody else. A custom role,
    // or a system role the platform does not depend on, can be deleted once
    // nobody holds it, and only by somebody who can edit roles.
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      alwaysVisible: true,
      mobileLabel: '',
      cell: (r) => (
        <RowActions>
          {canEdit
            ? <IconAction label={`Edit ${r.name}`} icon={Pencil} onClick={() => setEditing(r)} />
            : <IconAction label={`View ${r.name}`} icon={Eye} onClick={() => setEditing(r)} />}
          {canEdit && r.deletable && r.holders === 0 && (
            <IconAction label={`Delete ${r.name}`} icon={Trash2} danger onClick={() => setDeleting(r)} />
          )}
        </RowActions>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roles"
        hint="A role is a named bundle of permissions. Changing one changes what everybody holding it can do — including to medical records — so every change needs a reason and is recorded."
        actions={canEdit && (
          <Button onClick={() => setCreating(true)} icon={<Plus className="h-4 w-4" aria-hidden />}>
            New role
          </Button>
        )}
      />

      <FilterBar
        filters={[
          {
            key: 'scopeType',
            label: 'Reach',
            // Drawn from the roles actually listed rather than from the
            // realm map: `PHYSICIAN` is SELF-scoped and belongs to the
            // platform, so the reaches on offer cannot be derived from the
            // realm alone without either hiding or inventing one.
            options: (Object.keys(SCOPE_LABEL) as ScopeType[])
              .filter((s) => realmRoles.some((r) => r.scopeType === s))
              .map((s) => ({ value: s, label: SCOPE_LABEL[s] })),
          },
          {
            key: 'kind',
            label: 'Kind',
            options: [
              { value: 'system', label: 'System' },
              { value: 'custom', label: 'Custom' },
            ],
          },
        ]}
        value={filters}
        onChange={setFilters}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search roles"
          aria-label="Search roles"
          className="w-full sm:w-64"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={roles.loading}
        error={roles.error}
        onRetry={roles.reload}
        columnsKey="admin.roles"
        total={realmRoles.length}
        onRowClick={setEditing}
        empty={{ title: 'No roles match', hint: 'Clear the filters to see the whole catalogue.' }}
      />

      {(editing || creating) && catalogue.data && (
        <RoleEditor
          role={editing}
          catalogue={catalogue.data}
          held={held}
          canEdit={held.has('system.settings-manage')}
          realm={realm}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={(message) => {
            toast.toast('success', message);
            setEditing(null);
            setCreating(false);
            roles.reload();
            catalogue.reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          what="The role is removed. Permissions themselves are untouched — this only removes the bundle."
          affects={
            deleting.holders > 0
              ? `${deleting.holders} ${deleting.holders === 1 ? 'person holds' : 'people hold'} this role and would lose it.`
              : 'Nobody currently holds this role.'
          }
          irreversible={deleting.holders > 0
            ? 'A deleted role cannot be restored, and the people holding it are left with no role — able to sign in and do nothing — until someone assigns another. Its audit history stays.'
            : 'A deleted role cannot be restored. Its audit history stays.'}
          confirmLabel="Delete role"
          tone="danger"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await api.del(`/admin/roles/${deleting.id}`);
            toast.toast('success', `${deleting.name} deleted.`);
            setDeleting(null);
            roles.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * The permission matrix.
 *
 * Grouped by domain rather than shown as one list of eighty-six checkboxes,
 * because the question somebody arrives with is about an area of the product
 * — "what can this role do with certificates" — not about an alphabet.
 */
function RoleEditor({ role, catalogue, held, canEdit, realm, onClose, onSaved }: {
  role: RoleRow | null;
  catalogue: PermissionDomain[];
  held: ReadonlySet<string>;
  canEdit: boolean;
  /** Which realm's roles this console administers. Bounds the reach picker. */
  realm: Realm;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const creating = role === null;
  const [key, setKey] = useState('');
  const [name, setName] = useState(role?.name ?? '');
  // A new role reaches as far as this console's own realm does. Defaulting to
  // TENANT put a platform administrator's new role in another realm, where the
  // screen that created it could not list it.
  const defaultScope: ScopeType = realm === 'platform' ? 'GLOBAL' : realm === 'venue' ? 'PARTNER' : 'TENANT';
  const [scopeType, setScopeType] = useState<ScopeType>(role?.scopeType ?? defaultScope);
  const [chosen, setChosen] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const original = useMemo(() => new Set(role?.permissions ?? []), [role]);
  const added = [...chosen].filter((p) => !original.has(p));
  const removed = [...original].filter((p) => !chosen.has(p));
  const changed = added.length > 0 || removed.length > 0;

  /**
   * The two clinical invariants, predicted here so the reader is told before
   * they write a reason and press save rather than after. The API enforces
   * them regardless — this is courtesy, not the control.
   */
  const conflict = useMemo(() => {
    if (chosen.has('lab.result-enter') && chosen.has('lab.result-release')) {
      return 'A laboratory cannot release a report on the technician’s own signature. '
        + 'Entering a result and releasing one must stay in different roles.';
    }
    if (chosen.has('physician.certify') && (role?.key ?? key) !== 'PHYSICIAN') {
      return 'Only the certifying surgeon may declare an employee fit. physician.certify '
        + 'belongs to the PHYSICIAN role alone.';
    }
    if (role?.key === 'PHYSICIAN' && !chosen.has('physician.certify')) {
      return 'Removing physician.certify from PHYSICIAN would leave nobody on the platform '
        + 'able to sign a fitness certificate.';
    }
    return null;
  }, [chosen, role, key]);

  function toggle(permission: string) {
    if (!canEdit) return;
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission); else next.add(permission);
      return next;
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (creating) {
        await api.post('/admin/roles', {
          key, name, scopeType, permissions: [...chosen],
        });
        onSaved(`${name} created.`);
      } else {
        if (changed) {
          await api.put(`/admin/roles/${role!.id}/permissions`, {
            permissions: [...chosen], reason,
          });
        }
        if (renamed) {
          await api.patch(`/admin/roles/${role!.id}`, { name: name.trim() });
        }
        onSaved(`${name} updated.`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const renamed = !creating && name.trim() !== role!.name;
  const blocked = Boolean(conflict)
    || (creating ? !key || !name.trim() : (!changed && !renamed) || (changed && reason.trim().length < 3) || !name.trim());

  return (
    <Dialog
      wide
      title={creating ? 'New role' : canEdit ? `Edit ${role!.name}` : role!.name}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button onClick={save} loading={busy} disabled={blocked}>
              {creating ? 'Create role' : changed ? `Save for ${role!.holders} ${role!.holders === 1 ? 'person' : 'people'}` : 'Save changes'}
            </Button>
          )}
        </>
      )}
    >
      <div className="mt-4 space-y-4">
        {creating && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Name"
              required
              value={name}
              onChange={setName}
              placeholder="Regional EHS Manager"
              autoFocus
            />
            <TextField
              label="Key"
              required
              mono
              value={key}
              onChange={(v) => setKey(v.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
              placeholder="REGIONAL_EHS"
              hint="Capitals and underscores. It cannot change later."
            />
            <SelectField
              label="How far it reaches"
              value={scopeType}
              onChange={(v) => setScopeType(v as ScopeType)}
              className="sm:col-span-2"
            >
                {/* Only reaches this console administers: a role created with
                    a reach from another realm would vanish from the list the
                    moment it was saved. This picker is only rendered while
                    creating, so an existing role's own reach — `PHYSICIAN` is
                    SELF-scoped and administered from the platform — is never
                    at stake here. */}
                {(Object.keys(SCOPE_LABEL) as ScopeType[])
                  .filter((s) => REALM_OF_SCOPE[s] === realm)
                  .map((s) => (
                    <option key={s} value={s}>{SCOPE_LABEL[s]}</option>
                  ))}
            </SelectField>
          </div>
        )}
        {/* A custom role can be renamed; a system role's name is what the
            platform's own screens and seed data call it. */}
        {!creating && canEdit && !role!.isSystem && (
          <TextField label="Name" required value={name} onChange={setName} />
        )}

        {!creating && role!.isSystem && (
          <Alert tone="warning">
            <Lock className="mr-1.5 inline h-4 w-4 align-text-bottom" />
            This is a system role. It can be tuned, but not below what the platform needs of it
            {role!.deletable ? ', and it can be deleted only while nobody holds it' : ', and it cannot be deleted'}. {role!.holders} {role!.holders === 1 ? 'person holds' : 'people hold'} it.
          </Alert>
        )}

        {conflict && (
          <Alert tone="danger">
            <ShieldAlert className="mr-1.5 inline h-4 w-4 align-text-bottom" />
            {conflict}
          </Alert>
        )}

        <p className="text-caption text-neutral-600">
          {chosen.size} permissions ticked.{' '}
          <InfoTip label="About the boxes you cannot tick">
            A box you cannot tick is one you do not hold yourself — nobody can grant more than they have.
          </InfoTip>
        </p>
        <div className="max-h-[45vh] space-y-4 overflow-y-auto rounded-card border border-neutral-200 p-3">
          {catalogue.map((domain) => (
            <fieldset key={domain.domain}>
              <legend className="text-caption font-semibold uppercase tracking-wider text-neutral-600">
                {domain.domain}
              </legend>
              <div className="mt-1.5 grid gap-1 sm:grid-cols-2">
                {domain.permissions.map((p) => {
                  // A permission the editor does not hold cannot be granted —
                  // the API refuses it — so the box is disabled and says why,
                  // rather than ticking and failing on save.
                  const grantable = held.has(p.key)
                    || held.has(`${domain.domain}.*`) || held.has('*');
                  const wasAlready = original.has(p.key);
                  const locked = !canEdit || (!grantable && !wasAlready);
                  return (
                    <label
                      key={p.key}
                      title={locked && !canEdit
                        ? 'You do not have permission to edit roles'
                        : locked
                          ? 'You cannot grant a permission you do not hold yourself'
                          : undefined}
                      className={`flex min-h-[28px] max-md:min-h-10 items-start gap-2 rounded px-1.5 py-1 text-small ${
                        locked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-neutral-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={chosen.has(p.key)}
                        disabled={locked}
                        onChange={() => toggle(p.key)}
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="min-w-0">
                        <span className={`font-mono ${locked ? 'text-neutral-500' : 'text-neutral-800'}`}>{p.key}</span>
                        {p.stepUp && (
                          <span
                            title="Needs two-factor authentication"
                            className="ml-1.5 rounded-badge bg-primary-100 px-1 text-caption text-primary-700"
                          >
                            needs 2FA
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        {!creating && changed && (
          <>
            <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small">
              {added.length > 0 && (
                <div><span className="text-success-600">Adding:</span> {added.join(', ')}</div>
              )}
              {removed.length > 0 && (
                <div><span className="text-danger-600">Removing:</span> {removed.join(', ')}</div>
              )}
              <div className="mt-1 text-caption text-neutral-500">
                <Users className="mr-1 inline h-3.5 w-3.5 align-text-bottom" />
                Takes effect for {role!.holders} {role!.holders === 1 ? 'person' : 'people'} the
                next time they sign in.
              </div>
            </div>

            <Field label="Why is this changing?" required hint="Recorded in the audit trail with the before and after.">
              {(p) => (
                <Textarea {...p} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
              )}
            </Field>
          </>
        )}

        {error && <ErrorBanner message={error} />}
      </div>
    </Dialog>
  );
}
